import { useState } from "react"
import { useTranslation } from "react-i18next"
import { Bell, Check, Trash2 } from "lucide-react"
import { useNotifications, type Notification } from "@/hooks/use-notifications"
import { NotificationDetailDialog } from "@/components/notifications/NotificationDetailDialog"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Button } from "@/components/ui/button"
import { ScrollArea } from "@/components/ui/scroll-area"
import { cn } from "@/lib/utils"
import { formatDistanceToNow } from "date-fns"
import { es, enUS } from "date-fns/locale"

export function NotificationsDropdown() {
    const { t, i18n } = useTranslation()
    const { notifications, unreadCount, markAsRead, markAllAsRead, clearAll } = useNotifications()
    const [detailOpen, setDetailOpen] = useState(false)
    const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null)

    // Resolve date-fns locale from i18next current language
    const dateFnsLocale = i18n.language?.startsWith('es') ? es : enUS

    const handleNotificationClick = (notification: Notification) => {
        markAsRead(notification.id)
        setSelectedNotification(notification)
        setDetailOpen(true)
    }

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="rounded-full bg-card/50 shrink-0 relative hover:bg-muted transition-colors">
                        <Bell className="h-5 w-5" />
                        {unreadCount > 0 && (
                            <div className="absolute top-0 right-0 h-2.5 w-2.5 bg-primary rounded-full border-2 border-background animate-pulse" />
                        )}
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                    align="center"
                    sideOffset={12}
                    collisionPadding={16}
                    className="w-[calc(100vw-32px)] sm:w-96 shadow-2xl border-border/50 bg-background/95 backdrop-blur-md"
                >
                    <DropdownMenuLabel className="flex items-center justify-between py-2">
                        <span className="font-semibold">{t('notifications.title')}</span>
                        <div className="flex items-center gap-1">
                            {unreadCount > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => markAllAsRead()}
                                    className="text-xs h-7 px-2 hover:bg-primary/10 hover:text-primary transition-colors"
                                >
                                    <Check className="mr-1 h-3 w-3" />
                                    {t('notifications.markAllRead')}
                                </Button>
                            )}
                            {notifications.length > 0 && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => clearAll()}
                                    className="text-xs h-7 px-2 hover:bg-destructive/10 hover:text-destructive transition-colors"
                                >
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            )}
                        </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <ScrollArea className={cn(notifications.length === 0 ? "h-28" : "h-[350px]")}>
                        {notifications.length === 0 ? (
                            <div className="p-8 text-center flex flex-col items-center gap-2 text-muted-foreground">
                                <Bell className="h-8 w-8 opacity-20" />
                                <span className="text-sm">{t('notifications.empty')}</span>
                            </div>
                        ) : (
                            <div className="flex flex-col p-1 pb-4">
                                {notifications.map((notification) => (
                                    <button
                                        key={notification.id}
                                        className={cn(
                                            "flex flex-col items-start gap-1 p-3 mx-1 my-0.5 rounded-lg transition-all duration-200 text-left group relative border border-transparent",
                                            !notification.is_read
                                                ? "bg-muted/40 hover:bg-muted border-primary/5 shadow-sm"
                                                : "hover:bg-accent hover:border-border/50"
                                        )}
                                        onClick={() => handleNotificationClick(notification)}
                                    >
                                        <div className="flex items-start gap-4 w-full">
                                            <div className="flex-1 space-y-1 min-w-0">
                                                <p className={cn(
                                                    "text-sm font-semibold leading-tight pr-4",
                                                    !notification.is_read ? "text-foreground" : "text-muted-foreground group-hover:text-foreground"
                                                )}>
                                                    {notification.title}
                                                </p>
                                                <p className="text-xs text-muted-foreground leading-relaxed whitespace-pre-wrap">
                                                    {notification.message}
                                                </p>
                                                <p className="text-[10px] text-muted-foreground/60 pt-1">
                                                    {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: dateFnsLocale })}
                                                </p>
                                            </div>
                                            {!notification.is_read && (
                                                <div className="h-2 w-2 rounded-full bg-primary mt-1.5 shrink-0 animate-in zoom-in duration-300" />
                                            )}
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </ScrollArea>
                </DropdownMenuContent>
            </DropdownMenu>

            <NotificationDetailDialog
                open={detailOpen}
                onOpenChange={setDetailOpen}
                notification={selectedNotification}
            />
        </>
    )
}
